import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ScheduleRunItemAction,
  type SchedulePreviewItem,
  type ScheduleStore,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { groupPreviewItems, type PreviewFilters, type PreviewGroup } from '../lib/preview-groups';

import { RunCounts } from './run-counts';
import { RunItemRow } from './run-item-row';

const ALL = '__all__';
/** Rows a store section renders at first, and adds per "Show more". */
const STEP = 100;
/** Up to this many items every store starts open; above it only the first one does. */
const OPEN_ALL_UP_TO = 60;

function StoreSection({
  group,
  storeName,
  defaultOpen,
}: {
  group: PreviewGroup;
  storeName: string;
  defaultOpen: boolean;
}) {
  const { t } = useTranslation('schedules');
  const [open, setOpen] = useState(defaultOpen);
  const [shown, setShown] = useState(STEP);
  const rest = group.items.length - shown;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
        >
          <ChevronRight
            aria-hidden
            className={cn('size-4 shrink-0 transition-transform', open && 'rotate-90')}
          />
          <span className="font-medium">{storeName}</span>
          <span className="text-xs">
            <RunCounts counts={group.counts} />
          </span>
        </button>
      </CollapsibleTrigger>
      {/* Rows mount only while open and a page at a time, so thousands of items stay smooth. */}
      <CollapsibleContent>
        {open ? (
          <div className="border-t">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('runs.columns.product')}</TableHead>
                  <TableHead>{t('runs.columns.store')}</TableHead>
                  <TableHead>{t('runs.columns.action')}</TableHead>
                  <TableHead>{t('runs.columns.reason')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.items.slice(0, shown).map((item) => (
                  <RunItemRow
                    key={`${item.storeId}:${item.productId}`}
                    item={item}
                    storeName={storeName}
                  />
                ))}
              </TableBody>
            </Table>
            {rest > 0 ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="mx-3 mb-2 px-0"
                onClick={() => setShown((n) => n + STEP)}
              >
                {t('preview.showMore', { count: Math.min(rest, STEP) })}
              </Button>
            ) : null}
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

export interface PreviewItemsProps {
  items: readonly SchedulePreviewItem[];
  stores: readonly ScheduleStore[];
}

/**
 * The preview's would-be changes, one collapsible section per store with its counts per action,
 * filtered by store and action like the run detail sheet.
 */
export function PreviewItems({ items, stores }: PreviewItemsProps) {
  const { t } = useTranslation('schedules');
  const [filters, setFilters] = useState<PreviewFilters>({ storeId: null, action: null });
  const storeNames = useMemo(() => new Map(stores.map((s) => [s.storeId, s.storeName])), [stores]);
  const groups = useMemo(
    () =>
      groupPreviewItems(
        items,
        filters,
        stores.map((s) => s.storeId),
      ),
    [filters, items, stores],
  );
  const openAll = items.length <= OPEN_ALL_UP_TO;

  return (
    <div className="flex flex-col gap-3" data-tour="schedules.detail.preview-items">
      <div className="flex flex-wrap gap-2">
        <Select
          value={filters.storeId ?? ALL}
          onValueChange={(value) =>
            setFilters((f) => ({ ...f, storeId: value === ALL ? null : value }))
          }
        >
          <SelectTrigger className="w-52" aria-label={t('runs.filterStore')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('runs.allStores')}</SelectItem>
            {stores.map((store) => (
              <SelectItem key={store.storeId} value={store.storeId}>
                {store.storeName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.action ?? ALL}
          onValueChange={(value) =>
            setFilters((f) => ({
              ...f,
              action: value === ALL ? null : (value as ScheduleRunItemAction),
            }))
          }
        >
          <SelectTrigger className="w-44" aria-label={t('runs.filterAction')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('runs.allActions')}</SelectItem>
            {Object.values(ScheduleRunItemAction).map((value) => (
              <SelectItem key={value} value={value}>
                {t(`action.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('preview.noItems')}</p>
      ) : (
        groups.map((group, index) => (
          <StoreSection
            key={`${group.storeId}:${filters.storeId ?? ''}:${filters.action ?? ''}`}
            group={group}
            storeName={storeNames.get(group.storeId) ?? t('runs.removedStore')}
            defaultOpen={openAll || index === 0}
          />
        ))
      )}
    </div>
  );
}
